import React, { useCallback, useState, useEffect } from 'react';
import * as Styles from './SiteBuilderPreview.styles';
import {
  Button, IconAiStar, IconArrow, IconDesktop, IconMobile, IconTablet,
  ProgressBar,
  Tooltip,
} from 'gatorcomponents';
import { DEVICE, DEVICE_SIZES } from './SiteBuilderPreview.enum';
import { useResize } from '@/hooks/useResize';

const SiteBuilderPreview = ({
  locales,
  projectUid,
  createPreviewLoading,
  handleSyncPreviewWithAiBuilder,
  handleGenerateNewSite,
  createSiteState,
  redirectSuccessPageAiBuilder,
}) => {
  const [device, setDevice] = useState(DEVICE.DESKTOP);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingTitle, setLoadingTitle] = useState(locales.loadingTitles[0]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;
  const iframeSrc = `https://ai.hostgator-builder.cloud/project/view/${projectUid}`;
  const isLoading = createPreviewLoading || (createSiteState && createSiteState.loading);

  const updateTitleWithTransition = useCallback(({ newTitle = null, newProgress = null }) => {
    newTitle && setIsTransitioning(true);

    setTimeout(() => {
      newTitle && setLoadingTitle(newTitle);
      newProgress && setLoadingProgress(newProgress);

      setTimeout(() => {
        newTitle && setIsTransitioning(false);
      }, 150);
    }, 150);
  }, []);

  const updateLoadingProgress = useCallback(() => {
    const isPublishing = createSiteState && createSiteState.loading;
    const titles = isPublishing ? locales.loadingTitlesPublish : locales.loadingTitles;

    const timeout1 = setTimeout(() => updateTitleWithTransition({ newProgress: 5 }), 5000);
    const timeout2 = setTimeout(() => updateTitleWithTransition({ newTitle: titles[1], newProgress: 20 }), 8000);
    const timeout3 = setTimeout(() => updateTitleWithTransition({ newTitle: titles[2], newProgress: 60 }), 15000);
    const timeout4 = setTimeout(() => updateTitleWithTransition({ newTitle: titles[3], newProgress: 90 }), 25000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    };
  }, [updateTitleWithTransition, locales.loadingTitles, locales.loadingTitlesPublish, createSiteState]);

  const resetProgress = useCallback(() => {
    const isPublishing = createSiteState && createSiteState.loading;
    const titles = isPublishing ? locales.loadingTitlesPublish : locales.loadingTitles;

    setLoadingProgress(0);
    setLoadingTitle(titles[0]);
  }, [setLoadingProgress, setLoadingTitle, locales.loadingTitles, locales.loadingTitlesPublish, createSiteState]);

  const handleCloseOnSuccess = useCallback(() => {
    if (createSiteState.status === 'success') {
      redirectSuccessPageAiBuilder && redirectSuccessPageAiBuilder();
    }
  }, [
    createSiteState.status,
    redirectSuccessPageAiBuilder,
  ]);

  useEffect(() => {
    handleCloseOnSuccess();
  }, [handleCloseOnSuccess]);

  useEffect(() => {
    if (isLoading) {
      resetProgress();
      const cleanup = updateLoadingProgress();
      return cleanup;
    }
  }, [isLoading, resetProgress, updateLoadingProgress]);

  return (
    <Styles.Wrapper>
      <Styles.Header>
        {!isMobile && (
          <>
            <Tooltip content={locales.tooltips.computer} preferredPosition="bottomCenter">
              <Styles.ButtonDeviceWrapper onClick={() => setDevice(DEVICE.DESKTOP)}>
                <IconDesktop size="md" color={device === DEVICE.DESKTOP ? 'primaryMedium' : 'lowMedium'} />
              </Styles.ButtonDeviceWrapper>
            </Tooltip>

            <Tooltip content={locales.tooltips.tablet} preferredPosition="bottomCenter">
              <Styles.ButtonDeviceWrapper onClick={() => setDevice(DEVICE.TABLET)}>
                <IconTablet size="md" color={device === DEVICE.TABLET ? 'primaryMedium' : 'lowMedium'} />
              </Styles.ButtonDeviceWrapper>
            </Tooltip>

            <Tooltip content={locales.tooltips.mobile} preferredPosition="bottomCenter">
              <Styles.ButtonDeviceWrapper onClick={() => setDevice(DEVICE.MOBILE)}>
                <IconMobile size="md" color={device === DEVICE.MOBILE ? 'primaryMedium' : 'lowMedium'} />
              </Styles.ButtonDeviceWrapper>
            </Tooltip>
          </>
        )}

        <Styles.ButtonWrapper>
          <Button
            iconRight={<IconAiStar color={isLoading ? 'lowLight' : 'primaryBlue'} />}
            disabled={isLoading}
            label={locales.ctaButtons.generateNewSite}
            onClick={() => {
              resetProgress();
              handleGenerateNewSite();
            }}
            size="small"
            variant="secondary"
            version="v2"
          />

          <Button
            iconRight={<IconArrow direction="right" />}
            disabled={isLoading}
            label={locales.ctaButtons.useThisSite}
            onClick={() => {
              resetProgress();
              handleSyncPreviewWithAiBuilder();
            }}
            size="small"
            variant="primary"
            version="v2"
          />
        </Styles.ButtonWrapper>
      </Styles.Header>

      {isLoading && (
        <Styles.ProgressBarContainer>
          <Styles.ProgressInner>
            <Styles.LoadingTitle $isTransitioning={isTransitioning}>
              {loadingTitle}
            </Styles.LoadingTitle>
            <ProgressBar
              progress={loadingProgress}
              progressWithBorderRadius
              size="lg"
              color="gradientGreenBlue"
            />
          </Styles.ProgressInner>
        </Styles.ProgressBarContainer>
      )}

      {!isLoading && (
        <Styles.Content>
          <Styles.Iframe src={iframeSrc} $width={DEVICE_SIZES[device]} />
        </Styles.Content>
      )}
    </Styles.Wrapper>
  );
};

export default SiteBuilderPreview;
